/** @format */

import { useState, useEffect } from "react";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc,
  writeBatch,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase/config";
import toast from "react-hot-toast";

const useCourseClassrooms = (courseId) => {
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch classrooms associated with this course
  const fetchCourseClassrooms = async () => {
    if (!courseId) return;

    setLoading(true);
    setError(null);

    try {
      // Get classrooms from the course's Classrooms subcollection
      const courseClassroomsRef = collection(
        db,
        "courses",
        courseId,
        "Classrooms"
      );
      const querySnapshot = await getDocs(courseClassroomsRef);

      const classroomsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log("Course classrooms data:", classroomsData);
      setClassrooms(classroomsData);
    } catch (err) {
      console.error("Error fetching course classrooms:", err);
      setError("Failed to fetch course classrooms");
    } finally {
      setLoading(false);
    }
  };

  // Add classroom to course
  const addClassroomToCourse = async (classroomData) => {
    if (!courseId) {
      toast.error("Course ID is required");
      return { success: false, error: "Course ID is required" };
    }

    setOperationLoading(true);
    setError(null);

    try {
      // Add classroom to course's Classrooms subcollection
      const courseClassroomsRef = collection(
        db,
        "courses",
        courseId,
        "Classrooms"
      );
      const newClassroomRef = await addDoc(courseClassroomsRef, {
        ...classroomData,
        addedAt: new Date().toISOString(),
        status: "active",
      });

      // Refresh classrooms list
      await fetchCourseClassrooms();

      toast.success("Classroom added to course successfully");
      return { success: true, classroomId: newClassroomRef.id };
    } catch (err) {
      console.error("Error adding classroom to course:", err);
      setError("Failed to add classroom to course");
      toast.error("Failed to add classroom to course");
      return { success: false, error: err.message };
    } finally {
      setOperationLoading(false);
    }
  };

  // Remove classroom from course
  const removeClassroomFromCourse = async (classroomId) => {
    if (!courseId || !classroomId) {
      toast.error("Course ID and Classroom ID are required");
      return {
        success: false,
        error: "Course ID and Classroom ID are required",
      };
    }

    setOperationLoading(true);
    setError(null);

    try {
      // Remove classroom from course's Classrooms subcollection
      const classroomRef = doc(
        db,
        "courses",
        courseId,
        "Classrooms",
        classroomId
      );
      await deleteDoc(classroomRef);

      // Refresh classrooms list
      await fetchCourseClassrooms();

      toast.success("Classroom removed from course successfully");
      return { success: true };
    } catch (err) {
      console.error("Error removing classroom from course:", err);
      setError("Failed to remove classroom from course");
      toast.error("Failed to remove classroom from course");
      return { success: false, error: err.message };
    } finally {
      setOperationLoading(false);
    }
  };

  // Update classroom status in course
  const updateClassroomStatus = async (classroomId, status) => {
    if (!courseId || !classroomId) {
      toast.error("Course ID and Classroom ID are required");
      return {
        success: false,
        error: "Course ID and Classroom ID are required",
      };
    }

    setOperationLoading(true);
    setError(null);

    try {
      // Update classroom status in course's Classrooms subcollection
      const classroomRef = doc(
        db,
        "courses",
        courseId,
        "Classrooms",
        classroomId
      );
      await updateDoc(classroomRef, {
        status,
        updatedAt: new Date().toISOString(),
      });

      // Refresh classrooms list
      await fetchCourseClassrooms();

      toast.success("Classroom status updated successfully");
      return { success: true };
    } catch (err) {
      console.error("Error updating classroom status:", err);
      setError("Failed to update classroom status");
      toast.error("Failed to update classroom status");
      return { success: false, error: err.message };
    } finally {
      setOperationLoading(false);
    }
  };

  // Bulk add classrooms to course
  const bulkAddClassrooms = async (classroomIds) => {
    if (!courseId || !classroomIds.length) {
      toast.error("Course ID and classroom IDs are required");
      return {
        success: false,
        error: "Course ID and classroom IDs are required",
      };
    }

    setOperationLoading(true);
    setError(null);

    try {
      const batch = writeBatch(db);
      const courseClassroomsRef = collection(
        db,
        "courses",
        courseId,
        "Classrooms"
      );

      for (const classroomId of classroomIds) {
        const newClassroomRef = doc(courseClassroomsRef);
        batch.set(newClassroomRef, {
          classroomId,
          addedAt: new Date().toISOString(),
          status: "active",
        });
      }

      await batch.commit();

      // Refresh classrooms list
      await fetchCourseClassrooms();

      toast.success(
        `${classroomIds.length} classrooms added to course successfully`
      );
      return { success: true, addedCount: classroomIds.length };
    } catch (err) {
      console.error("Error bulk adding classrooms to course:", err);
      setError("Failed to bulk add classrooms to course");
      toast.error("Failed to bulk add classrooms to course");
      return { success: false, error: err.message };
    } finally {
      setOperationLoading(false);
    }
  };

  // Set up real-time listener for course classrooms
  useEffect(() => {
    if (!courseId) return;

    const courseClassroomsRef = collection(
      db,
      "courses",
      courseId,
      "Classrooms"
    );

    const unsubscribe = onSnapshot(
      courseClassroomsRef,
      (snapshot) => {
        const classroomsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log("Real-time course classrooms data:", classroomsData);
        setClassrooms(classroomsData);
        setError(null);
      },
      (err) => {
        console.error("Error listening to course classrooms:", err);
        setError("Failed to listen to course classrooms");
      }
    );

    return () => unsubscribe();
  }, [courseId]);

  return {
    classrooms,
    loading,
    operationLoading,
    error,
    fetchCourseClassrooms,
    addClassroomToCourse,
    removeClassroomFromCourse,
    updateClassroomStatus,
    bulkAddClassrooms,
    refetch: fetchCourseClassrooms,
  };
};

export default useCourseClassrooms;
